import {
  DEVIATION_ROW_ID,
  SITE_PERFORMANCE_CHART_COLUMN_LABEL_ONE,
  SITE_PERFORMANCE_CHART_COLUMN_LABEL_TWO,
} from '../constants/SitePerformanceChart.constants';
import {
  SitePerformanceChartTooltipProps,
  SitePerformanceChartTooltipRow,
} from '../components/SitePerformanceChartTooltip/SitePerformanceChartTooltip.types';
import { NO_SITE_ASSIGNED_BAR_ID } from './SitePerformanceChartUtils';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { DateTime } from 'lib/utils/date-handling/DateTime';
import { ReactChartUtils } from 'app/utils/react-charts/ReactChartUtils';

export const buildTooltipRows = ({
  siteIndex,
  focusedSeriesIndex,
  pageActualData,
  pagePlannedData,
  isGcd,
  t,
}: SitePerformanceChartTooltipProps): SitePerformanceChartTooltipRow[] => {
  const actualDatum = pageActualData[siteIndex];
  const plannedDatum = pagePlannedData[siteIndex];
  // Unassigned machines have no site to report operating hours against — actual/planned are inapplicable, not zero.
  const isUnassignedMachinesBar = ReactChartUtils.extractColumnId(actualDatum?.primary) === NO_SITE_ASSIGNED_BAR_ID;

  const tooltipGroup: {
    seriesId: string;
    seriesLabel: string;
    seriesIndex: number;
    secondaryValue: number;
  }[] = [];

  if (actualDatum) {
    tooltipGroup.push({
      seriesId: SITE_PERFORMANCE_CHART_COLUMN_LABEL_ONE,
      seriesLabel: SITE_PERFORMANCE_CHART_COLUMN_LABEL_ONE,
      seriesIndex: 0,
      secondaryValue: actualDatum.secondary,
    });
  }

  if (isGcd && plannedDatum) {
    tooltipGroup.push({
      seriesId: SITE_PERFORMANCE_CHART_COLUMN_LABEL_TWO,
      seriesLabel: SITE_PERFORMANCE_CHART_COLUMN_LABEL_TWO,
      seriesIndex: 1,
      secondaryValue: plannedDatum.secondary,
    });
  }

  const actualMs = DateTime.roundMsDurationToMinutes({
    ms: tooltipGroup.find(item => item.seriesLabel === SITE_PERFORMANCE_CHART_COLUMN_LABEL_ONE)?.secondaryValue || 0,
  });

  const plannedMs = DateTime.roundMsDurationToMinutes({
    ms: tooltipGroup.find(item => item.seriesLabel === SITE_PERFORMANCE_CHART_COLUMN_LABEL_TWO)?.secondaryValue || 0,
  });

  let deviationMs = 0;
  let deviationPercent = t('common.NA');
  let deviationNotAvailable = true;

  if (plannedMs && !isUnassignedMachinesBar) {
    deviationNotAvailable = false;
    deviationMs = actualMs - plannedMs;
    deviationPercent = [parseFloat(((deviationMs * 100) / plannedMs).toFixed(0)), '%'].join('');
  }

  const rows: SitePerformanceChartTooltipRow[] = [...tooltipGroup]
    .sort((a, b) => a.seriesLabel.localeCompare(b.seriesLabel))
    .map(colData => {
      const { seriesLabel, seriesId, secondaryValue, seriesIndex } = colData;
      const isPlanned = seriesLabel === SITE_PERFORMANCE_CHART_COLUMN_LABEL_TWO;

      return {
        seriesId,
        seriesLabel,
        hoursTitle: isUnassignedMachinesBar
          ? t('common.NA')
          : [
              DateTime.formatDurationByMilliseconds({ ms: secondaryValue, showSeconds: false }),
              t('operatingHoursComparisonChart.hUnit'),
            ].join(''),
        percent: isUnassignedMachinesBar
          ? t('common.NA')
          : `${secondaryValue && plannedMs ? parseFloat(((secondaryValue * 100) / plannedMs).toFixed(0)) : 0}%`,
        isHighlight: seriesIndex === focusedSeriesIndex,
        colorSquareIcon: isPlanned ? kccThemeConstants.COLORS.GRAY_250 : kccThemeConstants.COLORS.PETROL[500],
      };
    });

  rows.push({
    seriesId: DEVIATION_ROW_ID,
    seriesLabel: 'operatingHoursComparisonChart.deviation',
    hoursTitle: deviationNotAvailable
      ? t('common.NA')
      : [
          DateTime.formatDurationByMilliseconds({ ms: deviationMs || 0, showSeconds: false }),
          t('operatingHoursComparisonChart.hUnit'),
        ].join(''),
    percent: deviationPercent,
    isHighlight: true,
    colorSquareIcon: 'transparent',
  });

  return rows;
};
